import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { companyBase } from '../lib/paths';
import { Badge, Button, ErrorBanner } from '../components/ui';

/**
 * The admin assistant.
 *
 * It can read the whole register and queue changes to it. It cannot change
 * anything: every write creates a proposal that appears on Review. The UI says so
 * permanently rather than in a dismissible notice, because the one dangerous
 * misunderstanding available here is a user believing a change has been made.
 *
 * Conversation state lives in this component and is sent back each turn. The server
 * stores nothing, so a cap table conversation does not become a second record of
 * who owns what with its own retention rules. The cost is that reloading the page
 * loses the thread, which is the right trade for this data.
 */

const SUGGESTIONS = [
  'Who owns this company, fully diluted?',
  'How much is left in the option pool?',
  'What happens to each holder if we exit at $50M?',
  'Does the register still reconcile with the ledger?',
];

export default function Agent() {
  const { companyId, role } = useCompany();
  const [turns, setTurns] = useState([]);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [queued, setQueued] = useState(0);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns, busy]);

  async function send(text) {
    const message = (text ?? draft).trim();
    if (!message || busy) return;

    setDraft('');
    setError(null);
    setBusy(true);
    setTurns((t) => [...t, { role: 'user', text: message }]);

    try {
      const result = await api.agentChat(companyId, message, history);
      // The server returns the full conversation including tool traffic; keep it
      // verbatim so the next turn has the same context the model just had.
      setHistory(result.messages);
      setTurns((t) => [
        ...t,
        {
          role: 'assistant',
          text: result.reply,
          tools: result.toolCalls ?? [],
          proposed: result.proposed ?? [],
        },
      ]);
      setQueued((n) => n + (result.proposed?.length ?? 0));
    } catch (err) {
      setError(err);
      // The failed turn is removed rather than left hanging: resending is clearer
      // than a conversation containing a question that was never answered.
      setTurns((t) => t.slice(0, -1));
      setDraft(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack agent-page">
      <div className="page-head">
        <div>
          <h1>Assistant</h1>
          <p>
            Ask about the cap table, or ask for a change. It reads live data and cites what
            it finds. <strong>It cannot change the cap table.</strong> Anything you ask it to
            do is queued on <Link to={companyBase(companyId, 'review')}>Review</Link> for you
            to approve.
          </p>
        </div>
        {queued > 0 ? (
          <div className="page-actions">
            <Link to={companyBase(companyId, 'review')}>
              <Badge tone="info">
                {queued} queued this session
              </Badge>
            </Link>
          </div>
        ) : null}
      </div>

      <ErrorBanner error={error} />

      <div className="chat">
        {turns.length === 0 ? (
          <div className="chat-empty">
            <p className="muted">
              It has read access to the register, the documents and the review queue. Roles
              are still enforced: it can only see this company, and approving anything is
              still your decision.
            </p>
            <div className="chat-suggestions">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="chip" onClick={() => send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {turns.map((turn, i) => (
          <div key={i} className={`bubble bubble-${turn.role}`}>
            {turn.role === 'assistant' && turn.tools?.length ? (
              <div className="bubble-tools">
                {turn.tools.map((t, j) => (
                  <span key={j} className={`tool-chip${t.ok ? '' : ' is-error'}`}>
                    {t.name.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            ) : null}

            <div className="bubble-text">{turn.text}</div>

            {turn.proposed?.length ? (
              <div className="bubble-proposed">
                <strong>
                  {turn.proposed.length} change{turn.proposed.length === 1 ? '' : 's'} queued for
                  your approval.
                </strong>{' '}
                Nothing has been applied.{' '}
                <Link to={companyBase(companyId, 'review')}>Review {turn.proposed.length === 1 ? 'it' : 'them'}</Link>
              </div>
            ) : null}
          </div>
        ))}

        {busy ? (
          <div className="bubble bubble-assistant is-thinking">
            <span className="spinner-inline" aria-hidden="true" />
            reading the cap table
          </div>
        ) : null}

        <div ref={endRef} />
      </div>

      <form
        className="chat-input"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, shift+enter adds a line. Matches every chat surface people
            // already use, so it needs no explaining.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={
            role === 'OWNER' || role === 'ADMIN'
              ? 'Ask about the cap table, or describe a change to queue…'
              : 'Ask about the cap table…'
          }
          rows={2}
          disabled={busy}
        />
        <Button type="submit" variant="primary" loading={busy} disabled={!draft.trim()}>
          Send
        </Button>
      </form>
    </div>
  );
}
